/**
 * AnimalsSection — §6 Animals form section.
 *
 * The animal roster is now DATA-DRIVEN: the approved animals (and the count)
 * come from the AppFolio applicants' Pets snapshot captured in
 * buildDefaultFormState (form.pets / form.animalCount). Staff no longer enter a
 * count. This section gives staff control over:
 *   - the permitted toggle (auto-defaulted from the application, still overridable)
 *   - animalRentPerAnimal (cents)
 *   - animalDepositPerAnimal (cents)
 *   - freeAnimalCount
 * (WEL-104: the RA-pending toggle was removed; RRA per-animal charge
 * exemptions surface here read-only via the chargeable-count helper text.)
 *
 * Per-animal deposit rolls into the singular Refundable Security Deposit
 * downstream in buildRenderConfig (§6.5), surfaced as an inline reminder here.
 */

import type { LeaseFormState, AnimalSuspect, PetResolution } from '@/lib/leaseConfig';
import { isPendingAccommodationAnimal, raExemptionBadge } from '@/lib/rraBuilderLabels';
import {
  ANIMAL_RENT_PER_ANIMAL_DEFAULT_CENTS,
  ANIMAL_DEPOSIT_PER_ANIMAL_DEFAULT_CENTS,
  chargeExemptAnimalCount,
  chargeableAnimalCount,
  petToRow,
} from '@/lib/leaseConfig';
import { formatMoneyCents } from '@/lib/money';
import { MoneyInput } from '@/components/MoneyInput';
import { IntInput } from '@/components/IntInput';
import { PendingDecisionMarks } from '@/components/PendingDecisionMarks';
import { markableAnimalsFor, type ServerMarkableAnimal } from '@/lib/rraPendingDecision';

/** WEL-104: the per-animal build-time RA designation control's wiring
 *  (ConfigurePage owns the API calls + draft-state stamping). */
export interface RaExemptionControls {
  /** Index of the pet a toggle is in flight for, else null (disables the row). */
  busyIndex: number | null;
  /** Last toggle error (e.g. not on RRA_STAFF), shown under the list. */
  error: string | null;
  /** Toggle the designation for form.pets[petIndex] on/off. */
  onToggle: (petIndex: number, next: boolean) => void;
}

interface AnimalsSectionProps {
  form: LeaseFormState;
  update: <K extends keyof LeaseFormState>(key: K, value: LeaseFormState[K]) => void;
  /** Possible-duplicate animals across co-applicants awaiting a staff decision. */
  suspects?: AnimalSuspect[];
  /** Record a staff decision on a suspect pair (merge → 'same', keep → 'different'). */
  onResolveSuspect?: (key: string, decision: PetResolution) => void;
  /** WEL-104 per-animal "Approved reasonable accommodation" control. Absent →
   *  the section renders exactly as before (read-only builds, no groupId). */
  raExemption?: RaExemptionControls;
  /** RRA v2.1 (Decision 2): the animals of an OPEN accommodation request (the review summary's
   *  names). Their $0 is a pending exemption, labelled as open — never as approved. */
  pendingAccommodationAnimalNames?: readonly string[];
  /** RRA v2.2 part B: the review summary reports an open ANIMAL accommodation request — show the
   *  `Decision pending` mark per animal (writes `animalsPendingDecision`). Absent/false → no block. */
  animalRequestOpen?: boolean;
  /** The review summary's merged roster of markable animals (AppFolio pets + tenant-intake animals, exempt
   *  ones excluded). Absent → the draft's own pets are offered. */
  markableAnimals?: ReadonlyArray<ServerMarkableAnimal>;
}

/** One animal line inside a duplicate-review card, with its source applicant. */
function SuspectLine({ pet }: { pet: AnimalSuspect['a'] }) {
  const lead = pet.name || pet.type || '—';
  const detail = [pet.name ? pet.type : '', pet.weight, pet.age].filter(Boolean).join(' · ');
  return (
    <div>
      <strong>{lead}</strong>
      {detail ? ` — ${detail}` : ''} <span className="helper">· entered by {pet.applicant}</span>
    </div>
  );
}

export function AnimalsSection({
  form,
  update,
  suspects = [],
  onResolveSuspect,
  raExemption,
  pendingAccommodationAnimalNames = [],
  animalRequestOpen = false,
  markableAnimals,
}: AnimalsSectionProps) {
  const permitted = form.animals === 'yes';

  function handleToggle(next: 'yes' | 'no') {
    if (next === form.animals) return;
    if (next === 'yes') {
      // The count is data-derived from the Pets snapshot — don't synthesize one
      // here. Just flip the toggle on and seed the per-animal charge defaults.
      update('animals', 'yes');
      if (form.animalRentPerAnimal === 0) {
        update('animalRentPerAnimal', ANIMAL_RENT_PER_ANIMAL_DEFAULT_CENTS);
      }
      if (form.animalDepositPerAnimal === 0) {
        update('animalDepositPerAnimal', ANIMAL_DEPOSIT_PER_ANIMAL_DEFAULT_CENTS);
      }
    } else {
      update('animals', 'no');
      update('animalRentPerAnimal', 0);
      update('animalDepositPerAnimal', 0);
    }
  }

  // Coerce to finite numbers. A draft snapshotted before these fields existed
  // (or before the AppFolio Pets enrichment landed) can carry undefined/NaN,
  // which would otherwise render literal "NaN" in the helper text and totals.
  // animalCount is data-derived; freeAnimalCount is staff-entered but may be blank.
  const animalCount = Number.isFinite(form.animalCount) ? form.animalCount : 0;
  const freeAnimalCount = Number.isFinite(form.freeAnimalCount) ? form.freeAnimalCount : 0;
  // Chargeable = total − free − RRA-exempt (per-animal charge exemptions ride
  // the roster rows; 0 everywhere until an accommodation approval writes one).
  const exemptCount = chargeExemptAnimalCount(form);
  const chargeable = permitted ? chargeableAnimalCount(form) : 0;
  const rentTotal = chargeable * form.animalRentPerAnimal;
  const depositTotal = chargeable * form.animalDepositPerAnimal;

  return (
    <section className="card">
      <div className="card-header">
        <h3 className="card-title">Animals</h3>
      </div>

      <div className="cluster" style={{ gap: 'var(--space-5)' }}>
        {(['yes', 'no'] as const).map((v) => (
          <label key={v} className="choice">
            <input
              type="radio"
              className="radio"
              checked={form.animals === v}
              onChange={() => handleToggle(v)}
            />
            {v === 'yes' ? 'Animals permitted in lease' : 'No animals'}
          </label>
        ))}
      </div>

      {permitted && (
        <div className="card stack-sm" style={{ marginTop: 'var(--space-5)' }}>
          <div className="field">
            <span className="helper">
              {animalCount} {animalCount === 1 ? 'animal' : 'animals'} on file from the
              application. The approved animals are populated from the application data.
            </span>
          </div>

          {suspects.length > 0 && (
            <div
              className="card stack-sm"
              data-tone="warning"
              style={{ marginTop: 'var(--space-2)' }}
            >
              <strong>
                {suspects.length} possible duplicate{suspects.length === 1 ? '' : 's'} to review
              </strong>
              <span className="helper">
                Co-applicants sometimes list the same animal with small differences. Confirm whether
                each pair is the same animal or two different ones. Animals listed by the same
                applicant are always kept separate.
              </span>
              {suspects.map((s) => (
                <div key={s.key} className="card stack-sm" style={{ padding: 'var(--space-4)' }}>
                  <div>
                    <SuspectLine pet={s.a} />
                    <SuspectLine pet={s.b} />
                  </div>
                  <div className="cluster" style={{ gap: 'var(--space-3)' }}>
                    <button
                      type="button"
                      className="btn"
                      data-size="sm"
                      data-variant="secondary"
                      onClick={() => onResolveSuspect?.(s.key, 'same')}
                    >
                      Same animal
                    </button>
                    <button
                      type="button"
                      className="btn"
                      data-size="sm"
                      data-variant="ghost"
                      onClick={() => onResolveSuspect?.(s.key, 'different')}
                    >
                      Two different animals
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* WEL-104: per-animal build-time "approved reasonable accommodation"
              designation — an INTERNAL STAFF control. Checking it waives the
              animal's rent + deposit (via the per-animal charge exemption) and
              records the approval as an RRA case; NOTHING about it appears in
              the lease (counsel Rule H) — the $0 charge is the only visible
              effect. Rendered only when the page wires the control (editable
              draft with a group). */}
          {raExemption && (form.pets?.length ?? 0) > 0 && (
            <div className="field">
              <label>Reasonable accommodation (internal — not lease text)</label>
              <span className="helper">
                Use this when an animal is an already-approved accommodation (e.g. an
                existing tenant&rsquo;s ESA moving to a new property): its rent and deposit are
                waived now, and the approval is recorded in the Accommodations console when
                the lease is sent. The lease itself never mentions it.
              </span>
              <div className="stack-sm" style={{ marginTop: 'var(--space-2)' }}>
                {(form.pets ?? []).map((pet, i) => {
                  const row = petToRow(pet);
                  const label = [row.name || 'Unnamed animal', row.type, row.breed]
                    .filter(Boolean)
                    .join(' · ');
                  // Memorialized = a real per-animal RRA case (a sent lease or a
                  // console approval); managed in the console, not un-checkable
                  // here. A build-time note (raWaiveBuildTime) is still editable.
                  const memorialized =
                    typeof pet.chargeExemptionCaseItemId === 'string' &&
                    pet.chargeExemptionCaseItemId.length > 0;
                  const exempt = memorialized || pet.raWaiveBuildTime === true;
                  const pending = memorialized && isPendingAccommodationAnimal(row.name, pendingAccommodationAnimalNames);
                  const busy = raExemption.busyIndex === i;
                  return (
                    <div
                      key={`${row.name}|${row.type}|${i}`}
                      className="cluster"
                      style={{ gap: 'var(--space-3)', alignItems: 'center', flexWrap: 'wrap' }}
                    >
                      <label className="choice" style={{ marginBottom: 0 }}>
                        <input
                          type="checkbox"
                          className="check"
                          checked={exempt}
                          disabled={raExemption.busyIndex !== null || memorialized}
                          onChange={(e) => raExemption.onToggle(i, e.target.checked)}
                        />
                        {pending ? (
                          <span>
                            <strong>{label}</strong>
                          </span>
                        ) : (
                          <span>
                            <strong>{label}</strong> — approved reasonable accommodation
                            (waive rent &amp; deposit)
                          </span>
                        )}
                      </label>
                      {busy && <span className="helper">Saving…</span>}
                      {exempt && !busy && (
                        <span className="badge" data-size="sm" data-tone={pending ? 'info' : 'success'}>
                          {raExemptionBadge({ memorialized, pending })}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
              {raExemption.error && (
                <div
                  role="alert"
                  style={{
                    color: 'var(--danger)',
                    fontSize: 'var(--text-sm)',
                    marginTop: 'var(--space-2)',
                  }}
                >
                  {raExemption.error}
                </div>
              )}
            </div>
          )}

          {/* RRA v2.2 part B (WEL-180): while an ANIMAL accommodation request is open, staff mark the
              animal(s) it concerns. They are $0 now; the lease prints a neutral pending-decision clause
              with the if-not-approved amounts below. Internal control — the lease never says why. */}
          <PendingDecisionMarks
            animals={markableAnimalsFor(markableAnimals, form.pets ?? [])}
            marked={form.animalsPendingDecision}
            rates={form}
            animalRequestOpen={animalRequestOpen}
            onChange={(next) => update('animalsPendingDecision', next)}
          />

          <div className="field">
            <label htmlFor="free-animal-count">Animals at no charge</label>
            <IntInput
              id="free-animal-count"
              min={0}
              max={animalCount}
              value={freeAnimalCount}
              onChange={(n) => update('freeAnimalCount', n)}
              ariaLabel="Number of animals waived from rent and deposit"
            />
            <span className="helper">
              {chargeable} chargeable {chargeable === 1 ? 'animal' : 'animals'} ({animalCount}{' '}
              total − {freeAnimalCount} free
              {exemptCount > 0 ? ` − ${exemptCount} exempt` : ''})
            </span>
          </div>

          <div className="field">
            <label>Monthly rent per animal</label>
            <MoneyInput
              valueCents={form.animalRentPerAnimal}
              onChangeCents={(c) => update('animalRentPerAnimal', c)}
              ariaLabel="Monthly rent per animal"
            />
            <span className="helper">
              Total monthly animal rent: {formatMoneyCents(rentTotal)} ({chargeable} ×{' '}
              {formatMoneyCents(form.animalRentPerAnimal)})
            </span>
          </div>

          <div className="field">
            <label>Security deposit per animal</label>
            <MoneyInput
              valueCents={form.animalDepositPerAnimal}
              onChangeCents={(c) => update('animalDepositPerAnimal', c)}
              ariaLabel="Security deposit per animal"
            />
            <span className="helper">
              Total animal deposit: {formatMoneyCents(depositTotal)} ({chargeable} ×{' '}
              {formatMoneyCents(form.animalDepositPerAnimal)})
            </span>
          </div>

          {/* WEL-104: the "Animal-related reasonable accommodation request pending"
              toggle was removed. Reasonable-accommodation handling is INTERNAL
              policy per counsel Rule H (ANIM-03) and never appears on the lease;
              the RA-pending lease paragraph was already removed, leaving this
              checkbox a no-op that misled staff. A separate internal RRA workflow
              tracks RA state. */}

          <div className="alert" data-tone="info" style={{ marginTop: 'var(--space-3)' }}>
            <div className="alert-body">
              The animal deposit is added to the base Refundable Security Deposit on the
              lease — it does not appear as a separate line item. Monthly animal rent is
              shown on its own row in the rent table.
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
